/**
 * Tour-wide money views built from the per-day Settlement sheets and Expense ledgers:
 * profit and loss, budget vs actuals, settlement reminders, and print/CSV output.
 * Pure functions only, so they run under node --test.
 */
import { NUM, formatUsd, settleTotals, ledgerTotals, findDuplicateExpenses, DEAL_PRESETS } from './calendarSettlement';

export const BUDGET_TAB = 'BUDGET';
export const BUDGET_DATE = 'tour';

function round2(n) {
  return Math.round(NUM(n) * 100) / 100;
}

function labelKey(label) {
  return String(label || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function parseJson(raw, fallback) {
  if (raw == null || String(raw).trim() === '') return fallback;
  try {
    const o = JSON.parse(String(raw));
    return o && typeof o === 'object' ? o : fallback;
  } catch {
    return fallback;
  }
}

export function emptyBudget() {
  return { lines: [] };
}

export function parseBudget(raw) {
  const o = parseJson(raw, emptyBudget());
  const lines = (Array.isArray(o.lines) ? o.lines : [])
    .map((l) => ({ label: String(l?.label || '').trim(), amount: NUM(l?.amount) }))
    .filter((l) => l.label || l.amount);
  return { lines };
}

export function serializeBudget(budget) {
  return JSON.stringify({ lines: Array.isArray(budget?.lines) ? budget.lines : [] });
}

/** Does this settlement sheet hold any real numbers? Empty presets should not count as a show. */
export function settlementHasData(sheet) {
  if (!sheet || typeof sheet !== 'object') return false;
  const t = settleTotals(sheet);
  return Boolean(t.gross || t.gtee || t.earned || t.paidToDate || NUM(sheet.deposit));
}

/**
 * @param {{ settlements?: Record<string, object>, ledgers?: Record<string, object>, legLedger?: object|null, budget?: object }} input
 *   settlements and ledgers are keyed by date key (parsed sheets, not JSON text).
 */
export function buildTourPnl({ settlements = {}, ledgers = {}, legLedger = null, budget = emptyBudget() } = {}) {
  const shows = Object.keys(settlements)
    .filter((k) => settlementHasData(settlements[k]))
    .sort()
    .map((dateKey) => {
      const t = settleTotals(settlements[dateKey]);
      return {
        dateKey,
        gross: round2(t.gross),
        net: round2(t.net),
        guarantee: round2(t.gtee),
        earned: round2(t.earned),
        paidToDate: round2(t.paidToDate),
        balance: round2(t.balance),
        disputed: t.disputedCount,
      };
    });

  const spend = new Map();
  let otherIncome = 0;
  let totalExpenses = 0;
  const addLedger = (ledger) => {
    if (!ledger || typeof ledger !== 'object') return;
    otherIncome += ledgerTotals({ income: ledger.income, expense: [] }).income;
    (Array.isArray(ledger.expense) ? ledger.expense : []).forEach((row) => {
      const amount = ledgerTotals({ income: [], expense: [row] }).expense;
      if (!amount) return;
      const key = labelKey(row?.label) || 'unlabeled';
      const entry = spend.get(key) || { label: String(row?.label || '').trim() || 'Unlabeled', amount: 0 };
      entry.amount += amount;
      spend.set(key, entry);
      totalExpenses += amount;
    });
  };
  Object.keys(ledgers).sort().forEach((k) => addLedger(ledgers[k]));
  addLedger(legLedger);

  const lines = Array.isArray(budget?.lines) ? budget.lines : [];
  const used = new Set();
  const budgetRows = lines.map((line) => {
    const key = labelKey(line.label);
    const actual = key && spend.has(key) ? spend.get(key).amount : 0;
    if (key) used.add(key);
    const planned = NUM(line.amount);
    return {
      label: line.label || 'Untitled',
      budget: round2(planned),
      actual: round2(actual),
      variance: round2(planned - actual),
      pct: planned > 0 ? Math.round((actual / planned) * 100) : null,
      over: planned > 0 && actual > planned,
    };
  });
  let unbudgeted = 0;
  spend.forEach((entry, key) => {
    if (!used.has(key)) unbudgeted += entry.amount;
  });
  if (unbudgeted > 0) {
    budgetRows.push({
      label: 'Not in budget',
      budget: 0,
      actual: round2(unbudgeted),
      variance: round2(-unbudgeted),
      pct: null,
      over: lines.length > 0,
      unbudgeted: true,
    });
  }
  const budgetTotal = lines.reduce((a, l) => a + NUM(l.amount), 0);

  const showIncome = shows.reduce((a, s) => a + s.earned, 0);
  const received = shows.reduce((a, s) => a + s.paidToDate, 0);
  const outstanding = shows.reduce((a, s) => a + s.balance, 0);
  const gross = shows.reduce((a, s) => a + s.gross, 0);
  const totalIncome = showIncome + otherIncome;
  const net = totalIncome - totalExpenses;
  return {
    shows,
    expenseCategories: [...spend.values()]
      .map((e) => ({ label: e.label, amount: round2(e.amount) }))
      .sort((a, b) => b.amount - a.amount),
    budgetRows,
    totals: {
      shows: shows.length,
      gross: round2(gross),
      showIncome: round2(showIncome),
      otherIncome: round2(otherIncome),
      totalIncome: round2(totalIncome),
      received: round2(received),
      outstanding: round2(outstanding),
      totalExpenses: round2(totalExpenses),
      net: round2(net),
      marginPct: totalIncome > 0 ? Math.round((net / totalIncome) * 100) : null,
      budget: round2(budgetTotal),
      budgetVariance: budgetTotal > 0 ? round2(budgetTotal - totalExpenses) : null,
      overBudget: budgetTotal > 0 && totalExpenses > budgetTotal,
    },
  };
}

function csvCell(value) {
  let text = value == null ? '' : String(value);
  if (/^[=+\-@\t]/.test(text) && !/^-?\d+(\.\d+)?$/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function tourPnlToCsv(pnl, meta = {}) {
  const rows = [];
  const add = (...cells) => rows.push(cells.map(csvCell).join(','));
  const money = (n) => round2(n).toFixed(2);
  add('Tour profit and loss');
  add('Tour', meta.tourName || '');
  rows.push('');
  add('Show date', 'Gross box office', 'Net box office', 'Guarantee', 'Artist earning', 'Paid to date', 'Balance due');
  pnl.shows.forEach((s) => add(s.dateKey, money(s.gross), money(s.net), money(s.guarantee), money(s.earned), money(s.paidToDate), money(s.balance)));
  rows.push('');
  add('Expense category', 'Amount');
  pnl.expenseCategories.forEach((e) => add(e.label, money(e.amount)));
  rows.push('');
  add('Budget line', 'Budget', 'Actual', 'Variance', '% used');
  pnl.budgetRows.forEach((b) => add(b.label, money(b.budget), money(b.actual), money(b.variance), b.pct == null ? '' : b.pct));
  rows.push('');
  const t = pnl.totals;
  add('Totals');
  add('Show income (artist earning)', money(t.showIncome));
  add('Other income', money(t.otherIncome));
  add('Total income', money(t.totalIncome));
  add('Total expenses', money(t.totalExpenses));
  add('Net profit', money(t.net));
  add('Received to date', money(t.received));
  add('Outstanding from promoters', money(t.outstanding));
  if (t.budget > 0) add('Budget', money(t.budget));
  return rows.join('\r\n');
}

/**
 * Things worth chasing, most urgent first.
 * @param {{ settlements?: Record<string, object>, todayKey: string, soonDays?: number }} input
 */
export function settlementReminders({ settlements = {}, todayKey, soonDays = 3 } = {}) {
  const out = [];
  const today = String(todayKey || '');
  const keys = Object.keys(settlements).sort();
  keys.forEach((dateKey) => {
    const sheet = settlements[dateKey];
    if (!settlementHasData(sheet)) return;
    const t = settleTotals(sheet);
    const past = today && dateKey < today;
    if (past && t.balance > 0.5) {
      out.push({ id: `bal-${dateKey}`, level: 'alert', dateKey, text: `${formatUsd(t.balance)} still owed for the ${dateKey} show.` });
    }
    if (past && t.earned > 0 && !(String(sheet.signedArtist || '').trim() && String(sheet.signedVenue || '').trim())) {
      out.push({ id: `sign-${dateKey}`, level: 'warn', dateKey, text: `The ${dateKey} settlement is not signed by both sides.` });
    }
    if (t.disputedCount > 0) {
      out.push({ id: `disp-${dateKey}`, level: 'warn', dateKey, text: `${t.disputedCount} disputed expense${t.disputedCount === 1 ? '' : 's'} (${formatUsd(t.disputedTotal)}) on ${dateKey}.` });
    }
    const dupes = findDuplicateExpenses(sheet.expenses);
    if (dupes.length) {
      out.push({ id: `dup-${dateKey}`, level: 'warn', dateKey, text: `Possible duplicate expense on ${dateKey}: ${dupes.map((d) => d.label).join(', ')}.` });
    }
    if (!past && today && t.gtee > 0 && !NUM(sheet.deposit) && !(Array.isArray(sheet.payments) && sheet.payments.length)) {
      const days = daysBetween(today, dateKey);
      if (days != null && days <= soonDays) {
        out.push({ id: `dep-${dateKey}`, level: 'warn', dateKey, text: `No deposit recorded for the ${dateKey} show (${days <= 0 ? 'today' : `in ${days} day${days === 1 ? '' : 's'}`}).` });
      }
    }
  });
  const rank = { alert: 0, warn: 1 };
  return out.sort((a, b) => rank[a.level] - rank[b.level] || a.dateKey.localeCompare(b.dateKey));
}

function daysBetween(fromKey, toKey) {
  const a = Date.parse(`${fromKey}T00:00:00Z`);
  const b = Date.parse(`${toKey}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86400000);
}

export function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const PRINT_CSS = `
  body { font-family: -apple-system, 'Helvetica Neue', Arial, sans-serif; color: #111; padding: 28px; max-width: 820px; margin: 0 auto; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 13px; letter-spacing: .08em; text-transform: uppercase; margin: 22px 0 6px; border-bottom: 2px solid #111; padding-bottom: 4px; }
  .sub { color: #555; font-size: 13px; margin-bottom: 8px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  th, td { text-align: left; padding: 5px 6px; border-bottom: 1px solid #ddd; }
  td.n, th.n { text-align: right; font-variant-numeric: tabular-nums; }
  tr.total td { font-weight: 700; border-top: 2px solid #111; }
  .over { color: #b00020; font-weight: 700; }
  .sign { display: flex; gap: 40px; margin-top: 36px; }
  .sign div { flex: 1; border-top: 1px solid #111; padding-top: 4px; font-size: 12px; }
  @media print { body { padding: 0; } }
`;

function printDocument(title, body) {
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>${PRINT_CSS}</style></head><body>${body}<script>window.onload=function(){setTimeout(function(){window.print();},250);};</script></body></html>`;
}

function table(head, rows, { numericFrom = 1, totalRow = null } = {}) {
  const cell = (tag, v, i) => `<${tag}${i >= numericFrom ? ' class="n"' : ''}>${v}</${tag}>`;
  const headHtml = `<tr>${head.map((h, i) => cell('th', escapeHtml(h), i)).join('')}</tr>`;
  const bodyHtml = rows.map((r) => `<tr>${r.map((v, i) => cell('td', v, i)).join('')}</tr>`).join('');
  const totalHtml = totalRow ? `<tr class="total">${totalRow.map((v, i) => cell('td', v, i)).join('')}</tr>` : '';
  return `<table>${headHtml}${bodyHtml}${totalHtml}</table>`;
}

export function settlementToPrintHtml(sheet, totals, meta = {}) {
  const st = sheet && typeof sheet === 'object' ? sheet : {};
  const t = totals && typeof totals === 'object' ? totals : settleTotals(st);
  const e = escapeHtml;
  const parts = [];
  parts.push(`<h1>Settlement — ${e(meta.venue || 'Show')}</h1>`);
  parts.push(`<div class="sub">${e([meta.cityLine, meta.date].filter(Boolean).join(' · '))}</div>`);
  parts.push(`<div class="sub">${e(DEAL_PRESETS[st.preset]?.label || st.preset || '')} deal · guarantee ${e(formatUsd(st.guarantee))} · ${e(NUM(st.split))}% split${st.dealRef ? ` · memo ${e(st.dealRef)}` : ''}</div>`);
  parts.push(`<div class="sub">Promoter: ${e(st.promoter || '—')} · Venue rep: ${e(st.venueRep || '—')} · Artist rep: ${e(st.artistRep || '—')}</div>`);
  parts.push('<h2>Box office</h2>');
  parts.push(table(
    ['Ticket tier', 'Sold', 'Price', 'Total'],
    (st.tiers || []).map((x) => [e(x.label), e(NUM(x.qty)), e(formatUsd(x.price)), e(formatUsd(NUM(x.qty) * NUM(x.price)))]),
    { totalRow: ['Gross', '', '', e(formatUsd(t.gross))] }
  ));
  parts.push('<h2>Deductions and expenses</h2>');
  const expRows = [
    ['Facility fees', '', e(formatUsd(t.facility))],
    ['Sales tax', '', e(formatUsd(t.tax))],
    ['Credit card fees', '', e(formatUsd(t.card))],
    ...(st.expenses || []).filter((x) => NUM(x.amount)).map((x) => [e(x.label), e(x.status || 'approved'), e(formatUsd(x.amount))]),
  ];
  parts.push(table(['Item', 'Status', 'Amount'], expRows, { numericFrom: 2, totalRow: ['Net box office', '', e(formatUsd(t.net))] }));
  parts.push('<h2>Payments</h2>');
  const payRows = [
    ['Deposit', e(formatUsd(st.deposit))],
    ...(st.payments || []).map((p) => [e(p.label || 'Payment'), e(formatUsd(p.amount))]),
  ];
  parts.push(table(['Payment', 'Amount'], payRows, { totalRow: ['Paid to date', e(formatUsd(t.paidToDate))] }));
  parts.push(table(['Result', 'Amount'], [
    ['Artist earning', e(formatUsd(t.earned))],
    ['Paid to date', e(formatUsd(t.paidToDate))],
  ], { totalRow: ['Balance due', e(formatUsd(t.balance))] }));
  if (st.payMethod) parts.push(`<div class="sub">Balance paid by: ${e(st.payMethod)}</div>`);
  if (st.notes) parts.push(`<h2>Notes</h2><div class="sub">${e(st.notes)}</div>`);
  parts.push(`<div class="sign"><div>Artist / tour manager: ${e(st.signedArtist || '')}</div><div>Promoter / venue: ${e(st.signedVenue || '')}</div><div>Date: ${e(st.signedDate || '')}</div></div>`);
  return printDocument(`Settlement ${meta.date || ''}`, parts.join(''));
}

export function tourPnlToPrintHtml(pnl, meta = {}) {
  const e = escapeHtml;
  const t = pnl.totals;
  const parts = [];
  parts.push(`<h1>Tour profit and loss${meta.tourName ? ` — ${e(meta.tourName)}` : ''}</h1>`);
  parts.push(`<div class="sub">${e(t.shows)} settled show${t.shows === 1 ? '' : 's'}</div>`);
  parts.push('<h2>Summary</h2>');
  parts.push(table(['Line', 'Amount'], [
    ['Show income (artist earning)', e(formatUsd(t.showIncome))],
    ['Other income', e(formatUsd(t.otherIncome))],
    ['Total expenses', e(formatUsd(t.totalExpenses))],
    ['Received to date', e(formatUsd(t.received))],
    ['Outstanding from promoters', e(formatUsd(t.outstanding))],
  ], { totalRow: ['Net profit', e(formatUsd(t.net))] }));
  if (pnl.shows.length) {
    parts.push('<h2>Shows</h2>');
    parts.push(table(
      ['Date', 'Gross', 'Artist earning', 'Paid', 'Balance'],
      pnl.shows.map((s) => [e(s.dateKey), e(formatUsd(s.gross)), e(formatUsd(s.earned)), e(formatUsd(s.paidToDate)), e(formatUsd(s.balance))])
    ));
  }
  if (pnl.budgetRows.length) {
    parts.push('<h2>Budget vs actual</h2>');
    parts.push(table(
      ['Line', 'Budget', 'Actual', 'Variance', '% used'],
      pnl.budgetRows.map((b) => [
        e(b.label), e(formatUsd(b.budget)), e(formatUsd(b.actual)),
        b.over ? `<span class="over">${e(formatUsd(b.variance))}</span>` : e(formatUsd(b.variance)),
        e(b.pct == null ? '' : `${b.pct}%`),
      ])
    ));
  } else if (pnl.expenseCategories.length) {
    parts.push('<h2>Expenses by category</h2>');
    parts.push(table(['Category', 'Amount'], pnl.expenseCategories.map((x) => [e(x.label), e(formatUsd(x.amount))])));
  }
  return printDocument('Tour profit and loss', parts.join(''));
}

/** Web only. Opens a print window (the browser's Save as PDF finishes the job). */
export function openPrintWindow(html) {
  if (typeof window === 'undefined' || typeof window.open !== 'function') return false;
  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
}
